import api from "app/lib/api";
import endpoints from "static/endpoints";
import {
  REMINDER_ATTEMPTS_MAPPER,
  SCHEDULE_TYPE_PAYLOAD_MAPPER,
} from "static/constants";
import { interpolate } from "utils/string";
import { getFormattedDate } from "utils/date";
import { mapReminderCallType } from "utils/patientReminder.util";

const buildQueryParams = ({
  year,
  month,
  endDate,
  account,
  startDate,
  noShowType,
  manufacturer,
  scheduleType,
  subscription,
}) => {
  let queryParams = [];

  if (year) queryParams.push(`year=${encodeURIComponent(year)}`);
  if (month) queryParams.push(`month=${encodeURIComponent(month)}`);
  if (account) queryParams.push(`account=${encodeURIComponent(account)}`);
  if (scheduleType) {
    queryParams.push(
      `schedule_type=${encodeURIComponent(
        SCHEDULE_TYPE_PAYLOAD_MAPPER[scheduleType]
      )}`
    );
  }
  if (startDate) queryParams.push(`startDate=${encodeURIComponent(startDate)}`);
  if (endDate) queryParams.push(`endDate=${encodeURIComponent(endDate)}`);
  if (manufacturer)
    queryParams.push(`manufacturer=${encodeURIComponent(manufacturer)}`);

  if (noShowType) {
    queryParams.push(`noshow_type=${encodeURIComponent(noShowType)}`);
  }

  if (subscription) {
    queryParams.push(`subscription=${encodeURIComponent(subscription)}`);
  }

  return queryParams;
};

export const getNonCompliantPatients = async ({
  year,
  month,
  account,
  scheduleType,
  noShowType,
  page,
  pageSize,
  sorting,
  filters,
  subscription,
}) => {
  let url = endpoints.patient.getNonCompliantPatients;

  url += "?";

  const queryParams = buildQueryParams({
    year,
    month,
    account,
    scheduleType,
    noShowType,
    subscription,
  });
  if (queryParams.length > 0) {
    url += `${queryParams.join("&")}&`;
  }

  if (filters?.length > 0) {
    const filterQueries = filters
      ?.map((filter) => {
        const { id, value } = filter;
        const newDate = getFormattedDate(value, "YYYY-MM-DD");

        if (REMINDER_ATTEMPTS_MAPPER[id]) {
          return `${mapReminderCallType(id)}=${value}&`;
        }

        if (["expected_date", "late_date", "disconnected_at"].includes(id)) {
          return `${id}=${newDate}&`;
        }

        return `${id}=${value}&`;
      })
      ?.filter(Boolean)
      ?.join("");

    url += filterQueries;
  }

  if (sorting && sorting.length > 0) {
    sorting.forEach((field) => {
      url += `sort=${field?.id}&order=${field?.desc ? "desc" : "asc"}&`;
    });
  }

  url += `page=${page}&pageSize=${pageSize}`;

  const { data } = await api.get(url.toString());
  return data;
};

export const getPatientReminderHistory = async (patientId) => {
  let url = interpolate(endpoints.patient.getPatientReminderHistory, {
    patientId,
  });

  if (patientId) {
    const { data } = await api.get(url.toString());
    return data;
  }
};

export const getNonEnrolledPatients = async (query) => {
  const { page, pageSize, filters, sorting } = query;
  let url = endpoints.patient.getNonEnrolledPatients;
  url += "?";

  let queryParams = [];

  if (queryParams.length > 0) {
    url += `${queryParams.join("&")}&`;
  }

  if (filters && filters.length > 0) {
    const filterQueries = filters
      ?.map((filter) => {
        const { id, value } = filter;
        const newDate = getFormattedDate(value, "YYYY-MM-DD");

        if (["doi", "lastSend"].includes(id)) {
          return `${id}=${newDate}&`;
        }

        return `${id}=${value}&`;
      })
      ?.filter(Boolean)
      ?.join("");

    url += filterQueries;
  }

  if (sorting?.length > 0) {
    const sortingQueries = sorting
      ?.map((sort) => {
        const { id, desc } = sort;
        const direction = desc ? "desc" : "asc";

        return `sort=${id}&order=${direction}&`;
      })
      ?.filter(Boolean)
      ?.join("");

    url += sortingQueries;
  }

  url += `page=${page}&pageSize=${pageSize}`;

  const { data } = await api.get(url);

  return data;
};

export const enrollNonEnrolledPatient = async (payload) => {
  return await api.post(endpoints.patient.enrollNonEnrolledPatient, payload);
};

export const addLiveCall = async (payload) => {
  const { patientId, ...rest } = payload;
  const url = interpolate(endpoints.patient.liveCall, {
    patientId,
  });

  return await api.post(url, rest);
};

export const updateLiveCall = async (payload) => {
  const { patientId, ...rest } = payload;
  const url = interpolate(endpoints.patient.liveCall, {
    patientId,
  });

  return await api.put(url, rest);
};

export const downloadOverduePatients = async (query) => {
  let url = endpoints.patient.downloadOverduePatients;
  url += "?";

  const queryParams = buildQueryParams(query);

  if (queryParams.length > 0) {
    url += `${queryParams.join("&")}&`;
  }

  return await api.get(url, {
    responseType: "blob",
  });
};
